import { useCallback, useEffect, useState } from "react";
import { getApiErrorMessage } from "../../../lib/result-codes";
import { getMerchantDetail } from "../api/merchant.api";
import type { MerchantDetail } from "../types/merchant.types";

interface UseGetDetailMerchantReturn {
  merchant: MerchantDetail | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useGetDetailMerchant = (
  id?: string,
  initialMerchant?: MerchantDetail | null,
): UseGetDetailMerchantReturn => {
  const [merchant, setMerchant] = useState<MerchantDetail | null>(initialMerchant ?? null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMerchantDetail = useCallback(async () => {
    if (!id) {
      setMerchant(initialMerchant ?? null);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const detail = await getMerchantDetail(id);
      setMerchant(detail);
    } catch (requestError: unknown) {
      setError(getApiErrorMessage(requestError, "Failed to load merchant detail"));
      if (!initialMerchant) {
        setMerchant(null);
      }
    } finally {
      setLoading(false);
    }
  }, [id, initialMerchant]);

  useEffect(() => {
    void fetchMerchantDetail();
  }, [fetchMerchantDetail]);

  return {
    merchant,
    loading,
    error,
    refetch: fetchMerchantDetail,
  };
};
